export default ({ componentId, name, channel, eventPrefix, copiedMessage, accepts = () => true }) => {
    const wire = window.Livewire.find(componentId);

    return {
        open: wire.entangle('isOpen'),
        output: wire.entangle('output'),
        isLoading: wire.entangle('isLoading').live,
        isSending: false,
        hasSentInSession: false,
        resizing: false,
        panelWidth: 420,
        panelHeight: 640,
        minWidth: 320,
        minHeight: 420,
        storageKey: `${name}-size`,
        init() {
            this.loadSize();

            window.addEventListener('resize', () => this.clampSize());

            Echo.private(channel)
                .listen(`${eventPrefix}.Generating`, (e) => {
                    if (!accepts(e)) {
                        return;
                    }

                    this.output += e.output;
                    this.scrollToBottom(true);
                })
                .listen(`${eventPrefix}.Finished`, (e) => {
                    if (!accepts(e)) {
                        return;
                    }

                    this.$dispatch(`${name}::${componentId}::response-finished`, {
                        output: e.output,
                        status: e.status,
                        conversationId: e.conversation_id,
                    });
                });

            this.$watch('open', (value) => {
                if (value) {
                    this.$nextTick(() => {
                        this.$refs.promptInput?.focus();
                        this.scrollToBottom();
                    });
                }
            });

            this.$watch('isLoading', (value) => {
                if (!value) {
                    this.isSending = false;
                }
            });

            this.$watch('$wire.prompt', (value) => {
                if (!value) {
                    this.$nextTick(() => {
                        const el = this.$refs.promptInput;
                        if (el) {
                            el.style.height = 'auto';
                        }
                    });
                }
            });
        },
        resize(event) {
            event.target.style.height = 'auto';
            event.target.style.height = event.target.scrollHeight + 'px';
        },
        loadSize() {
            try {
                const stored = JSON.parse(localStorage.getItem(this.storageKey));
                if (stored?.width && stored?.height) {
                    this.panelWidth = stored.width;
                    this.panelHeight = stored.height;
                }
            } catch {
                localStorage.removeItem(this.storageKey);
            }

            this.clampSize();
        },
        clampSize() {
            this.panelWidth = Math.min(Math.max(this.panelWidth, this.minWidth), this.maxWidth());
            this.panelHeight = Math.min(Math.max(this.panelHeight, this.minHeight), this.maxHeight());
        },
        isMobile() {
            return window.innerWidth < 640;
        },
        maxWidth() {
            return window.innerWidth - 48;
        },
        maxHeight() {
            return window.innerHeight - 24;
        },
        startResize(event) {
            event.preventDefault();
            event.stopPropagation();

            this.resizing = true;

            const point = event.touches ? event.touches[0] : event;
            const startX = point.clientX;
            const startY = point.clientY;
            const startWidth = this.panelWidth;
            const startHeight = this.panelHeight;
            const maxWidth = this.maxWidth();
            const maxHeight = this.maxHeight();

            const onMove = (e) => {
                const movePoint = e.touches ? e.touches[0] : e;
                this.panelWidth = Math.min(Math.max(startWidth - (movePoint.clientX - startX), this.minWidth), maxWidth);
                this.panelHeight = Math.min(Math.max(startHeight - (movePoint.clientY - startY), this.minHeight), maxHeight);
            };

            const onUp = () => {
                this.resizing = false;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend', onUp);
                localStorage.setItem(this.storageKey, JSON.stringify({
                    width: this.panelWidth,
                    height: this.panelHeight,
                }));
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend', onUp);
        },
        submitMessage() {
            if (!this.$wire.prompt.trim() || this.isSending) {
                return;
            }

            this.isSending = true;
            this.hasSentInSession = true;
            this.$wire.ask().then(() => {
                this.isSending = this.isLoading;
                this.scrollToBottom(true);
            });
        },
        onPressEnter(event) {
            if (event.shiftKey || this.isSending) {
                return;
            }

            event.preventDefault();

            this.submitMessage();
        },
        copyToClipboard(content) {
            navigator.clipboard.writeText(content);
            this.$dispatch('toast::show', { type: 'success', message: copiedMessage });
        },
        scrollToBottom(smooth = false) {
            this.$nextTick(() => {
                const container = this.$refs.messages;

                if (smooth) {
                    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                } else {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        get markedOutput() {
            return marked.parse(this.output ?? '', { breaks: true });
        },
    };
};
